import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { useGetBioLinkSettingsQuery } from '../../../../redux/api/fymAPI';
import { useEditorContext } from '../SMLEditorContext';
import { blockMessages as m } from './blockMessages';
import { useAutoAddNote } from './useAutoAddNote';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLAutoAddNoteProps {
  widgets: readonly SerializedWidget[];
}

/** "Swamps was added from your Catalog 2 minutes ago. New songs go under your profile. Change this" (design e3). */
const SMLAutoAddNoteComponent: React.FC<SMLAutoAddNoteProps> = ({ widgets }) => {
  const intl = useIntl();
  const { accountId, smartLinkId, url } = useEditorContext();
  const { newest, dismiss } = useAutoAddNote(smartLinkId, widgets);
  const { data } = useGetBioLinkSettingsQuery({ accountId }, { skip: newest === null });
  const openPage = useCallback(() => url.setTab('page'), [url]);

  if (newest === null || newest.widget.type !== SmartLinkWidgetType.presave) {
    return null;
  }
  const minutes = Math.round((Date.parse(newest.at) - Date.now()) / 60000);
  const when =
    Math.abs(minutes) < 60
      ? intl.formatRelativeTime(minutes, 'minute')
      : intl.formatRelativeTime(Math.round(minutes / 60), 'hour', { numeric: 'auto' });
  const where = intl.formatMessage(data?.settings.position === 'end' ? m.autoAddWhereEnd : m.autoAddWhereProfile);

  return (
    <div className={styles.note} role="status" data-testid="auto-add-note">
      <FontAwesomeIcon icon={faSparkles} className={styles.noteIcon} />
      <span className={styles.noteText}>
        {intl.formatMessage(m.autoAddNote, { title: newest.widget.data.trackTitle, when, where })}{' '}
        <button type="button" className={styles.noteLink} onClick={openPage}>
          {intl.formatMessage(m.autoAddChange)}
        </button>
      </span>
      <button
        type="button"
        className={styles.noteClose}
        onClick={dismiss}
        aria-label={intl.formatMessage(m.autoAddDismiss)}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
    </div>
  );
};

export const SMLAutoAddNote = TypedMemo(SMLAutoAddNoteComponent);
